import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { compare, Operation } from 'fast-json-patch'

import { TextInput } from '../input/TextInput'
import { Button } from '../buttons/Button'
import FormSection from '../formSection/FormSection'
import FormSectionActions from '../formSection/formSectionActions/FormSectionActions'

import { AddressModel } from '../../types/court/court'
import { hasCompleteBillingAddress } from '../../utils/address/address'

type AddressProps = {
  address: AddressModel | null
  title?: string
  subtitle?: string
  requiredOnly?: boolean
  loading?: boolean
  showActions?: boolean
  onSave?: (patch: Operation[]) => Promise<unknown> | unknown
  onSkip?: () => void
  saveLabel?: string
  skipLabel?: string
  onChange?: (address: AddressModel) => void
  onValidityChange?: (isValid: boolean) => void
}

type AddressField =
  | 'street'
  | 'number'
  | 'floor'
  | 'apartment'
  | 'city'
  | 'county'
  | 'postalCode'

const ADDRESS_FIELDS: AddressField[] = [
  'street',
  'number',
  'floor',
  'apartment',
  'city',
  'county',
  'postalCode',
]

const createFieldsValidity = (): Record<AddressField, boolean> =>
  Object.fromEntries(ADDRESS_FIELDS.map(field => [field, true])) as Record<
    AddressField,
    boolean
  >

const isAddressField = (name: string): name is AddressField =>
  ADDRESS_FIELDS.includes(name as AddressField)

const toAddressModel = (
  address?: Partial<AddressModel> | null
): AddressModel => ({
  id: address?.id,
  street: address?.street ?? '',
  number: address?.number ?? '',
  floor: address?.floor ?? '',
  apartment: address?.apartment ?? '',
  city: address?.city ?? '',
  county: address?.county ?? '',
  postalCode: address?.postalCode ?? '',
})

const Address = ({
  address,
  title,
  subtitle,
  requiredOnly = false,
  loading = false,
  showActions = true,
  onSave,
  onSkip,
  saveLabel,
  skipLabel,
  onChange,
  onValidityChange,
}: AddressProps) => {
  const { t } = useTranslation('address')
  const [details, setDetails] = useState<AddressModel>(() => toAddressModel())
  const [initialAddress, setInitialAddress] = useState<AddressModel | null>(
    null
  )
  const [fieldsValidity, setFieldsValidity] = useState<
    Record<AddressField, boolean>
  >(() => createFieldsValidity())

  useEffect(() => {
    const model = toAddressModel(address)

    if (initialAddress && compare(details, model).length === 0) {
      return
    }

    setInitialAddress(model)
    setDetails(model)
    setFieldsValidity(createFieldsValidity())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address])

  const patch = useMemo(() => {
    if (!initialAddress) {
      return []
    }

    return compare(initialAddress, details)
  }, [initialAddress, details])

  const patchIsValid = patch.every(operation => {
    const field = operation.path.slice(1)

    return isAddressField(field) && fieldsValidity[field]
  })

  const formIsValid =
    hasCompleteBillingAddress(details) &&
    Object.values(fieldsValidity).every(Boolean)

  useEffect(() => {
    onValidityChange?.(formIsValid)
  }, [formIsValid, onValidityChange])

  const handleSave = async () => {
    if (
      !onSave ||
      !patchIsValid ||
      !formIsValid ||
      patch.length === 0 ||
      loading
    ) {
      return
    }

    const result = await onSave(patch)

    if (result === false || result === undefined) {
      return
    }

    setInitialAddress(toAddressModel(details))
  }

  const handleReset = () => {
    if (!initialAddress) {
      return
    }

    setDetails(initialAddress)
    setFieldsValidity(createFieldsValidity())
    onChange?.(initialAddress)
  }

  const handleChange = (
    name: string,
    value: string | boolean | null,
    isValid = true
  ) => {
    if (typeof value !== 'string' || !isAddressField(name)) {
      return
    }

    setDetails(previous => {
      const next = {
        ...previous,
        [name]: value,
      }

      onChange?.(next)

      return next
    })

    setFieldsValidity(previous => ({
      ...previous,
      [name]: isValid,
    }))
  }

  return (
    <FormSection
      title={title}
      subtitle={subtitle}
      actions={
        showActions ? (
          onChange ? (
            <Button
              label={t('reset-btn')}
              variant="ghost"
              size="sm"
              block
              onClick={handleReset}
              disabled={patch.length === 0}
            />
          ) : (
            <FormSectionActions
              isDirty={patch.length > 0}
              isValid={patchIsValid && formIsValid}
              loading={loading}
              disableResetWhileLoading={false}
              showReset={!saveLabel || Boolean(onSkip)}
              enableSecondaryActionWhenUnchanged={Boolean(onSkip)}
              onSave={handleSave}
              onCancel={onSkip ?? handleReset}
              saveLabel={saveLabel ?? t('save-btn')}
              resetLabel={skipLabel ?? t('reset-btn')}
            />
          )
        ) : undefined
      }
    >
      <div className="row g-3">
        <div className="col-12 col-md-8">
          <TextInput
            name="street"
            label={t('street')}
            value={details.street}
            errorMessage={t('validation.required')}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col-12 col-md-4">
          <TextInput
            name="number"
            label={t('number')}
            value={details.number}
            errorMessage={t('validation.required')}
            onChange={handleChange}
            required
          />
        </div>
        {!requiredOnly && (
          <>
            <div className="col-12 col-md-6">
              <TextInput
                name="floor"
                label={t('floor')}
                value={details.floor ?? ''}
                onChange={handleChange}
              />
            </div>
            <div className="col-12 col-md-6">
              <TextInput
                name="apartment"
                label={t('apartment')}
                value={details.apartment ?? ''}
                onChange={handleChange}
              />
            </div>
          </>
        )}
        <div className="col-12 col-md-4">
          <TextInput
            name="city"
            label={t('city')}
            value={details.city}
            errorMessage={t('validation.required')}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col-12 col-md-4">
          <TextInput
            name="county"
            label={t('county')}
            value={details.county}
            errorMessage={t('validation.required')}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col-12 col-md-4">
          <TextInput
            name="postalCode"
            label={t('postal-code')}
            value={details.postalCode}
            errorMessage={t('validation.required')}
            onChange={handleChange}
            required
          />
        </div>
      </div>
    </FormSection>
  )
}

export default Address
